import { Component, EventEmitter, OnDestroy, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { debounceTime, filter, finalize, take } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';

import { EngagementService } from 'src/app/care/engagement/engagement.service';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { EngagementTemplateFlowStepEnum } from 'src/app/shared/components/choose-template-flow/shared/engagement-template-flow-step.enum';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { SiteV2 } from 'src/app/shared/models';
import { BasicService } from 'src/app/shared/services/basic.service';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { environment } from 'src/environments/environment';
import { ChooseTemplateFlowService } from '../shared/choose-template-flow.service';
import { EngagementUserIdTypeEnum } from '../shared/engagement-user-id-type.enum';

@Component({
  selector: 'app-choose-sender-recipient',
  templateUrl: './choose-sender-recipient.component.html',
  styleUrls: [
    '../../../../task-process/answer-editor/answer-editor.component.scss',
    './choose-sender-recipient.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ChooseSenderRecipientComponent implements OnDestroy {
  @Output() private getTemplatesForAccount: EventEmitter<SiteV2> = new EventEmitter();
  @Output() private tryToSearchConversation: EventEmitter<{ userExternalId: number; siteId: number }> =
    new EventEmitter();

  isExpanded = true;
  accounts: Choice[] = this.sitesService.sitesV2
    .filter(site => this.engagementService.allowedChannelTypeIds.includes(site.type))
    .map(this.sitesService.enrichSiteWithLogo);
  userCountryCode: string;
  consentCtrl = new FormControl(this.engagementService.strategy.isCRMMode);

  form: FormGroup = this.formBuilder.group({
    from: [{ value: null, disabled: this.engagementService.strategy.isCRMMode }, Validators.required],
    to: [null, Validators.required],
  });

  recipientForm: FormGroup = this.formBuilder.group({
    phoneNumber: [],
    appUserId: [],
    userKey: [],
  });

  checkingRecipient = false;

  /**
   * Stores the valid phone number.
   */
  phoneNumberObject: any;

  get taskProcessContext(): TaskProcessContext {
    return this.engagementService.strategy.taskProcessContext;
  }

  brandName = this.alcmeonContextService.self.companies[0].name;

  SiteTypeEnum = SiteTypeEnum;
  canNotChoose = true;

  EngagementUserIdTypeEnum = EngagementUserIdTypeEnum;

  private subscriptions = new Subscription();

  constructor(
    public engagementService: EngagementService,
    private sitesService: SitesService,
    private alcmeonContextService: AlcmeonContextService,
    private formBuilder: FormBuilder,
    private customRouter: CustomRouter,
    private chooseTemplateFlowService: ChooseTemplateFlowService,
    basicService: BasicService
  ) {
    let siteId: number;
    if (this.taskProcessContext?.site?.id) {
      siteId = this.taskProcessContext.site.id;
      this.engagementService.channelType = this.taskProcessContext.site['typeName'];
    } else {
      siteId = this.accounts[0]?.id as number;
      this.engagementService.channelType = this.accounts[0]['typeName'];
    }

    this.subscriptions.add(
      this.consentCtrl.valueChanges.subscribe(value => {
        this.canNotChoose = (this.form.invalid && !this.engagementService.strategy.externalUser) || !value;
      })
    );
    this.subscriptions.add(
      this.form.valueChanges.subscribe(() => {
        this.canNotChoose =
          (this.form.invalid && !this.engagementService.strategy.externalUser) || !this.consentCtrl.value;
      })
    );
    this.subscriptions.add(
      this.form.controls.from.valueChanges.subscribe(siteId => {
        this.consentCtrl.setValue(this.engagementService.strategy.isCRMMode);
        this.engagementService.clearContext();
        const site = sitesService.sitesV2Map.get(siteId);
        this.engagementService.channelType = site.typeName;
        this.taskProcessContext.site = this.sitesService.enrichSiteWithLogo(site);
        if (engagementService.allowedChannelTypes.includes(this.taskProcessContext.site['typeName'])) {
          this.getTemplatesForAccount.emit(this.taskProcessContext.site as SiteV2);
        }
      })
    );
    this.subscriptions.add(
      this.engagementService.strategy.initContextBS.subscribe((value: { siteId: number; userExternalId: string }) => {
        if (value) {
          this.form.patchValue({ to: value.userExternalId });

          const { userIdType, userExternalId } = engagementService.strategy;
          if (this.engagementService.channelType === SiteTypeEnum.Kakaotalk && userIdType) {
            this.recipientForm.reset();
            switch (userIdType) {
              case EngagementUserIdTypeEnum.APP_USER_ID:
                this.recipientForm.get('appUserId').setValue(userExternalId);
                break;
              case EngagementUserIdTypeEnum.USER_KEY:
                this.recipientForm.get('userKey').setValue(userExternalId);
                break;
              default:
                this.recipientForm.get('phoneNumber').setValue(userExternalId);
                break;
            }
          }
        }
      })
    );

    this.subscriptions.add(
      this.recipientForm.valueChanges.subscribe(value => {
        if (value) {
          const { phoneNumber, appUserId, userKey } = value;
          this.form.get('to').setValue(phoneNumber || appUserId || userKey);
        }
      })
    );

    this.form.controls.from.setValue(siteId, { emitEvent: !this.taskProcessContext?.site?.id });
    basicService.getUserCountryCode().subscribe(userCountryCode => (this.userCountryCode = userCountryCode));
    this.addPhoneInputListener();
    this.consentCtrl.setValue(this.engagementService.strategy.isCRMMode);
  }
  searchExternalUserByPhoneNumber() {
    this.searchExternalUser(EngagementUserIdTypeEnum.PHONE_NUMBER);
  }

  searchExternalUserByAppUserId() {
    this.searchExternalUser(EngagementUserIdTypeEnum.APP_USER_ID);
  }

  searchExternalUserByUserKey() {
    this.searchExternalUser(EngagementUserIdTypeEnum.USER_KEY);
  }

  searchExternalUser(engagementUserIdTypeEnum?: EngagementUserIdTypeEnum): void {
    const { from, to } = this.form.value;
    this.checkingRecipient = true;
    this.engagementService
      .tryToFindRecipient(from, to, engagementUserIdTypeEnum)
      .pipe(finalize(() => (this.checkingRecipient = false)))
      .subscribe({
        next: ({ name, id, external_id }) => {
          if (environment.bundledWithAngularJSApp) {
            let searchedExternalUser = (to || '').replace('+', '');
            if (this.engagementService.channelType === SiteTypeEnum.Kakaotalk) {
              searchedExternalUser = this.engagementService.computeUserExternalIds(searchedExternalUser);
            }
            this.customRouter.transitionTo(
              'auth.alcmeon.engagement.send-message.user-external-id',
              { siteId: from, userExternalId: searchedExternalUser },
              {
                reload: false,
                inherit: true,
                notify: false,
              }
            );
          }

          this.engagementService.strategy.userExternalId = to;
          if (id) {
            this.engagementService.strategy.externalUser = `${name} ${to}`;
          } else {
            this.engagementService.strategy.externalUser = `${to}`;
          }

          this.tryToSearchConversation.emit({
            siteId: from,
            userExternalId: external_id || to,
          });
        },
      });
  }

  chooseWriteMessage(): void {
    this.chooseTemplateFlowService.messageData = null;
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_WRITE_MESSAGE;
  }

  chooseTemplateMessage(): void {
    this.chooseTemplateFlowService.messageData = null;
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_TEMPLATE_MESSAGE;
  }

  private addPhoneInputListener() {
    this.subscriptions.add(
      interval(1000)
        .pipe(
          debounceTime(500),
          filter(() => !!document.querySelector('input.tel-input-container')),
          take(1)
        )
        .subscribe({
          next: () => {
            const input = document.querySelector('input.tel-input-container');
            input.addEventListener('keydown', ($event: any) => {
              if ($event) {
                const { key } = $event;
                if (key === 'Enter') {
                  input.dispatchEvent(new Event('blur'));
                  this.searchExternalUser();
                }
              }
            });
          },
        })
    );
  }

  clearResult() {
    this.engagementService.clearContext();
    this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_RECIPIENT_SENDER;
    this.form.get('to').reset();
    this.recipientForm.reset();
    this.addPhoneInputListener();
  }

  phoneInputChanged(value: string) {
    this.recipientForm.get('phoneNumber').setValue(value);
  }

  telInputObject(obj) {
    this.phoneNumberObject = obj;
  }

  telInputInit(): string {
    return this.recipientForm.value.phoneNumber;
  }

  isPhoneNumberButtonValid() {
    const valid = this.phoneNumberObject.isValidNumber();
    this.recipientForm.get('phoneNumber').setErrors(
      valid
        ? null
        : {
            'invalid-number': true,
          }
    );
    return valid;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
